import { IconTools } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { DashboardProjectDto } from 'src/Services/API/generated';
import { trackHomeCardLinkClicked } from 'src/Services/Mixpanel/events';

import { CardItem, TwoColumnCard } from '../TwoColumnCard';

import { ProjectThumbnail } from './ProjectThumbnail';

interface Props {
  projects: DashboardProjectDto[];
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(amount);

const ProjectsCard = ({ projects }: Props) => {
  const { t, i18n } = useTranslation();
  const assetId = useCurrentAssetId();

  const votes = projects.filter(project => project.hasActiveVote);
  const ongoing = projects.filter(project => !project.hasActiveVote);

  const buildVoteSubtitle = (project: DashboardProjectDto) => {
    const parts: string[] = [];
    if (project.voteDeadline) {
      parts.push(
        t('dashboard.cards.projects.voteUntil', 'vote jusqu’au {{date}}', {
          date: dayjs(project.voteDeadline).locale(i18n.language).format('D MMM'),
        })
      );
    }
    if (project.totalHouseholdsCount > 0) {
      parts.push(
        t('dashboard.cards.projects.householdsVoted', '{{voted}} / {{total}} foyers ont voté', {
          voted: project.votedHouseholdsCount,
          total: project.totalHouseholdsCount,
        })
      );
    }
    return parts.length > 0 ? parts.join(' · ') : undefined;
  };

  const buildOngoingSubtitle = (project: DashboardProjectDto) => {
    const parts: string[] = [];
    if (project.deadline) {
      parts.push(
        t('dashboard.cards.projects.deliveryOn', 'livraison {{date}}', {
          date: dayjs(project.deadline).locale(i18n.language).format('MMMM'),
        })
      );
    }
    return parts.length > 0 ? parts.join(' · ') : undefined;
  };

  const buildVoteItem = (project: DashboardProjectDto) => (
    <CardItem
      key={project.id}
      leading={<ProjectThumbnail title={project.title} imageUrl={project.imageUrl} hasVoteBadge />}
      title={project.title}
      subtitle={buildVoteSubtitle(project)}
      trailing={
        typeof project.estimatedCost === 'number'
          ? formatCurrency(project.estimatedCost)
          : undefined
      }
    />
  );

  const buildOngoingItem = (project: DashboardProjectDto) => (
    <CardItem
      key={project.id}
      leading={<ProjectThumbnail title={project.title} imageUrl={project.imageUrl} />}
      title={project.title}
      subtitle={buildOngoingSubtitle(project)}
      trailing={
        typeof project.estimatedCost === 'number'
          ? formatCurrency(project.estimatedCost)
          : undefined
      }
    />
  );

  const unvotedNotice = votes.find(project => !project.hasCurrentUserVoted);

  const voteItems: React.ReactNode[] = votes.map(buildVoteItem);
  if (unvotedNotice) {
    voteItems.push(
      <div key="unvoted" style={{ fontSize: 13, color: 'var(--mantine-color-yellow-8, #7a5c1e)' }}>
        {t('dashboard.cards.projects.pleaseVote', 'Vous n’avez pas encore voté.')}
      </div>
    );
  }

  return (
    <TwoColumnCard
      title={t('dashboard.cards.projects.title', 'Projets & travaux')}
      linkLabel={t('dashboard.cards.projects.link', 'Voir les projets')}
      linkTo={`/asset/${assetId}/project-and-works`}
      onLinkClick={() => trackHomeCardLinkClicked({ card: 'projects' })}
      accent="gold"
      icon={<IconTools size={20} stroke={1.75} />}
      left={{
        title: t('dashboard.cards.projects.votes', 'Soumis au vote'),
        count: votes.length,
        tone: 'vote',
        items: voteItems,
        empty: t('dashboard.cards.projects.votesEmpty', 'Aucun vote en cours'),
      }}
      right={{
        title: t('dashboard.cards.projects.ongoing', 'En cours'),
        count: ongoing.length,
        items: ongoing.map(buildOngoingItem),
        empty: t('dashboard.cards.projects.ongoingEmpty', 'Aucun projet en cours'),
      }}
    />
  );
};

export default ProjectsCard;
